import { ng } from '@uirouter/angularjs/lib/angular';
import * as angular from 'angular';
import { BlobService } from '../../../../../app.common/modules/common/services/blob.service';
import { DefinitionsService } from '../../../../../app.common/modules/common/services/definitions.service';
import { EnumService } from '../../../../../app.common/modules/common/services/enum.service';
import { NotificationService } from '../../../../../app.common/modules/common/services/notification.service';
import { RetailerProfile } from '../../../core';
import { JispPayService } from '../../../jispPay/jispPay.service';
import { ILanguageSettings } from '../../../model/ILanguageSettings';
import { AccountTypeEnum } from '../../../model/open360BusinessLogicRetailersAccountBERetailerProfile';
import { AccountService } from '../../account.service';

export interface IBusinessType {
    Name: string;
    ID: AccountTypeEnum;
}
export class ProfileController implements ng.IController {

    public static $inject = [
        'blobService',
        'definitionsService',
        'enumService',
        'accountService',
        'notificationService',
        'jispPayService',
        '$uibModal',
        '$translate'
    ];

    public permissionType: {} = this.enumService.RetailerPermission;
    public waiting: boolean = false;
    public profileLoaded: boolean = false;
    public profile: RetailerProfile = {
        ID: null,
        FirstName: null,
        LastName: null,
        CompanyName: null,
        phone: null,
        PhoneNumber: {
            PhoneNumber: null,
            CountryPhoneCodeID: null,
        },
        WhatsAppNumber: {
            PhoneNumber: null,
            CountryPhoneCodeID: null,
        },
        Email: null,
        CountryID: null,
        CurrencySign: null,
        CurrencyID: null,
        LanguageID: null,
        LogoID: null,
        Story: null,
        LoyaltyProgramEnabled: false
    };
    public profileForm: ng.IFormController;
    public languages: ILanguageSettings[] = [];
    public selectedLanguage: ILanguageSettings = null;
    public businessTypes: IBusinessType[];
    public selectedBusinessType: IBusinessType = null;
    public profileImage: string;
    public phoneNumberControl: string;
    public whatsAppNumberControl: string;

    private minSize = {
        w: 500,
        h: 500
    };

    private thumbSize = {
        w: 152,
        h: 152
    };

    constructor(
        private blobService: BlobService,
        public definitionsService: DefinitionsService,
        public enumService: EnumService,
        private accountService: AccountService,
        private notificationService: NotificationService,
        private jispPayService: JispPayService,
        private $uibModal: ng.ui.bootstrap.IModalService,
        private $translate: angular.translate.ITranslateService
    ) {}

    public async $onInit() {
        this.notificationService.showWaitingMessage();
        this.waiting = true;
        try {
            this.profile = await this.accountService.getProfile();
            if (this.profile.PhoneNumber) {
                this.phoneNumberControl = this.profile.PhoneNumber.PhoneNumberWithoutCode;
            }
            this.whatsAppNumberControl = this.profile.WhatsAppNumber ?
                this.profile.WhatsAppNumber.PhoneNumberWithoutCode
                : null;
            this.languages = await this.accountService.getLanguages();
            this.selectedLanguage = this.languages.find(
                (language) => language.ID === this.profile.LanguageID
            );
            this.profileLoaded = true;
            this.notificationService.clearAll();
            this.profileImage = await this.getImage();
        } catch (error) {
            console.log(error);
            this.notificationService.showErrorMessage(error);
        }
        this.waiting = false;
    }

    public setLoyaltyProgram(currentValue: boolean) {
        this.profile.LoyaltyProgramEnabled = !currentValue;
    }

    public async deleteProfileImage() {
        this.profileImage = null;
        this.profileForm.$setDirty();
    }

    public async openChangePasswordDialog() {
        try {
            await this.$uibModal.open({
                component: 'changeRetailerPassword',
                resolve: {},
                size: 'md',
                backdrop: 'static'
            }).result;
        } catch (e) {
            return;
        }
    }

    public async openChangeEmailDialog() {
        try {
            const newEmail = await this.$uibModal.open({
                component: 'changeRetailerEmail',
                resolve: {},
                size: 'md',
                backdrop: 'static'
            }).result;
            if (newEmail) {
                this.profile.Email = newEmail;
            }
        } catch (e) {
            return;
        }
    }

    public async updateProfile() {
        this.waiting = true;
        this.notificationService.showWaitingMessage();
        this.profile.LanguageID = this.selectedLanguage.ID;
        try {
            if (this.profile && this.profile.LogoID) {
                await this.accountService.deleteProfileImage(this.profile.LogoID.toString());
                this.profile.LogoID = null;
            }
            if (this.profileImage) {
                const logoID = await this.jispPayService.uploadImage({ dataUri: this.profileImage }, this.accountService.addProfileImage());
                if (logoID) {
                    this.profile.LogoID = logoID;
                }
            }
            const tempProfile = {
                ...this.profile,
                PhoneNumber: {
                    CountryPhoneCodeID: this.profile.PhoneNumber.CountryPhoneCodeID,
                    PhoneNumber: this.profile.PhoneNumber.PhoneNumberWithoutCode
                },
                WhatsAppNumber: this.profile.WhatsAppNumber.PhoneNumberWithoutCode ? {
                    CountryPhoneCodeID: this.profile.WhatsAppNumber.CountryPhoneCodeID,
                    PhoneNumber: this.profile.WhatsAppNumber.PhoneNumberWithoutCode
                } : null
            };
            await this.accountService.updateProfile(tempProfile);
            this.notificationService.showSuccessMessage();
            this.accountService.getProfile();
            this.profileImage = await this.getImage();
            this.profileForm.$setPristine();
        } catch (error) {
            this.notificationService.showErrorMessage(error);
        }
        finally {
            this.waiting = false;
        }
    }

    public async addImage() {
        try {
            const image = await this.$uibModal.open({
                component: 'jispPayAvatar',
                resolve: {
                    avatarId: () => this.profile.LogoID,
                    title: () => this.$translate.instant('profile_photo'),
                    sizeText: () =>
                        this.$translate.instant('profile_photo_min_size_hint'),
                    getUrl: () =>
                        this.profile.LogoID === null ||
                        this.getImage(),
                    imageSize: () => this.minSize
                },
                backdrop: 'static'
            }).result;
            if (image) {
                this.profileImage = image;
                this.profileForm.$setDirty();
            }
        } catch (error) {
            return;
        }
    }

    public async getImage() {
        if (this.profile.LogoID) {
            try {
                const currentImage = await this.blobService.getDataImageByUrl(
                    this.accountService.getProfileImage(this.profile.LogoID)
                );
                return currentImage.dataUri;
            } catch (error) {
                return;
            }
        }
        return null;
    }
}

angular.module('naut').component('profile', {
    template: require('./profile.html'),
    controller: ProfileController,
    controllerAs: 'ctrl'
});



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/account/components/profile/profile.component.ts